import { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import PDFGenerator from "./quote-pdf.component";
import PlanEstimates from "./plan-estimates.component";

const QuoteEditTool = ({
    currentUser,
    proposal,
    showQuoteEditTool,
    setShowQuoteEditTool,
    customPercentage,
    handleUpdatePercentage,
    onSessionExpired,
    onTokenRefresh
}) => {
    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const [formData, setFormData] = useState({
        userId: currentUser.id,
        advisorCompany: "",
        advisorName: "",
        advisorEmail: "",
        clientCompany: "",
        clientName: "",
        adminFee: "",
        province: "",
        companyType: "Incorporated",
        planAccount: "[]",
    });
    const [planAccounts, setPlanAccounts] = useState([]);

    useEffect(() => {
        if (proposal) {
            const parsedAccounts = JSON.parse(proposal.planAccount || "[]");
            setPlanAccounts(parsedAccounts);

            setFormData({
                ...formData,
                advisorCompany: proposal.advisorCompany || "",
                advisorName: proposal.advisorName || "",
                advisorEmail: proposal.advisorEmail || "",
                clientCompany: proposal.clientCompany || "",
                clientName: proposal.clientName || "",
                adminFee: proposal.adminFee || "",
                province: proposal.province || "",
                companyType: proposal.companyType?.trim() || "Incorporated",
            });
        }
    }, [proposal]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            planAccount: JSON.stringify(planAccounts),
        }));
    }, [planAccounts]);

    const createPlanAccount = (accountType) => ({
        accountType,
        accountTotalBudget: 0,
        benefitCategories: [{
            description: "",
            bencatBudget: "",
            limits: {
                single: { noOfEmployees: "", limitPerEmployee: "", limitPerEmployeeLSA: "" },
                couple: { noOfEmployees: "", limitPerEmployee: "", limitPerEmployeeLSA: "" },
                family: { noOfEmployees: "", limitPerEmployee: "", limitPerEmployeeLSA: "" }
            },
            excludeFromCoreHealth: false
        }]
    });

    const getAccountConversion = useCallback((newType, existingTypes) => {
        const hasType = (types) => types.some(t => existingTypes.includes(t));

        const conversionRules = {
            CoreHealth: [
                { if: ["HSA"], remove: ["HSA"], add: "CoreHealth" },
                { if: ["Flex"], remove: ["Flex"], add: "CHFlex" },
                { if: ["Fixed"], remove: ["Fixed"], add: "CHFixed" },
                { if: ["CHFlex"], remove: ["CHFlex"], add: "CoreHealth" },
                { if: ["CHFixed"], remove: ["CHFixed"], add: "CoreHealth" }
            ],
            Flex: [
                { if: ["HSA", "LSA"], remove: ["HSA", "LSA"], add: "Flex" },
                { if: ["Fixed"], remove: ["Fixed"], add: "Flex" },
                { if: ["CoreHealth"], remove: ["CoreHealth"], add: "CHFlex" },
                { if: ["CHFixed"], remove: ["CHFixed"], add: "CHFlex" }
            ],
            Fixed: [
                { if: ["HSA", "LSA"], remove: ["HSA", "LSA"], add: "Fixed" },
                { if: ["Flex"], remove: ["Flex"], add: "Fixed" },
                { if: ["CoreHealth"], remove: ["CoreHealth"], add: "CHFixed" },
                { if: ["CHFlex"], remove: ["CHFlex"], add: "CHFixed" }
            ]
        };

        const rules = conversionRules[newType];
        if (!rules) return null;

        for (const rule of rules) {
            if (hasType(rule.if)) {
                return { remove: rule.remove, add: rule.add };
            }
        }
        return null;
    }, []);

    const handleCheckboxChange = useCallback((event) => {
        const { value: newType, checked } = event.target;

        setPlanAccounts(prev => {
            const existingTypes = prev.map(p => p.accountType);

            if (checked) {
                const conversion = getAccountConversion(newType, existingTypes);

                if (conversion) {
                    return prev
                        .filter(p => !conversion.remove.includes(p.accountType))
                        .concat(createPlanAccount(conversion.add));
                }

                return [...prev, createPlanAccount(newType)];
            } else {
                const uncheckedRules = {
                    CoreHealth: [
                        { if: "CHFlex", add: "Flex" },
                        { if: "CHFixed", add: "Fixed" }
                    ],
                    Flex: [
                        { if: "CHFlex", add: "CoreHealth" }
                    ],
                    Fixed: [
                        { if: "CHFixed", add: "CoreHealth" }
                    ]
                };

                const rules = uncheckedRules[newType];
                if (rules) {
                    for (const rule of rules) {
                        const found = prev.find(p => p.accountType === rule.if);
                        if (found) {
                            return prev.map(p =>
                                p.accountType === rule.if
                                    ? createPlanAccount(rule.add)
                                    : p
                            );
                        }
                    }
                }

                return prev.filter(p => p.accountType !== newType);
            }
        });
    }, [getAccountConversion, createPlanAccount]);


    const updatePlanAccount = (index, updatedAccount) => {
        setPlanAccounts(prev =>
            prev.map((account, i) => i === index ? updatedAccount : account)
        );
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        try {
            const response = await authFetch(
                `${apiUrl}/proposals/edit/${proposal.id}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(formData),
                },
                authParams
            );

            const data = await response.json();

            if (response.ok) {
                toast.success(data.message);
                setShowQuoteEditTool(false);
            } else {
                toast.error(`Error: ${data.message}`);
            }
        } catch (error) {
            if (!(error instanceof SessionExpiredError)) {
                toast.error(`Error: ${error.message}`);
            }
        }
    };

    const isChecked = (type) => {
        const typeMap = {
            CoreHealth: ["CoreHealth", "CHFlex", "CHFixed", "CHF"],
            Flex: ["Flex", "CHFlex", "CHF"],
            Fixed: ["Fixed", "CHFixed"]
        };

        return planAccounts.some(p => typeMap[type]?.includes(p.accountType) || p.accountType === type);
    };

    const isDisabled = (type) => {
        const disableRules = {
            HSA: ["CoreHealth", "Flex", "CHFlex", "Fixed", "CHFixed"],
            LSA: ["Flex", "CHFlex", "Fixed", "CHFixed"]
        };

        return disableRules[type]?.some(t => planAccounts.some(p => p.accountType === t)) || false;
    };

    if (!showQuoteEditTool) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-modal w-full max-w-5xl max-h-[90vh] flex flex-col">
                {/* header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
                    <h3 className="text-lg font-semibold text-gray-900">Edit Quote</h3>
                    <button
                        onClick={() => setShowQuoteEditTool(false)}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* body */}
                <div className="flex-1 overflow-y-auto p-4">
                    <form id="quoteToolForm" onSubmit={handleSubmit}>
                        <div className="space-y-6">
                            {/* advisor information */}
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="bg-primary px-4 py-3">
                                    <h5 className="text-white font-semibold">Advisor Information</h5>
                                </div>
                                <div className="p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label htmlFor="advisorName" className="block text-sm font-semibold text-gray-700 mb-1">
                                                Name<span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="advisorName"
                                                name="advisorName"
                                                value={formData.advisorName}
                                                onChange={handleFormChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                placeholder="Enter the advisor's name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="advisorCompany" className="block text-sm font-semibold text-gray-700 mb-1">
                                                Company<span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="advisorCompany"
                                                name="advisorCompany"
                                                value={formData.advisorCompany}
                                                onChange={handleFormChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                placeholder="Enter the advisor's company"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="advisorEmail" className="block text-sm font-semibold text-gray-700 mb-1">
                                                Email<span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                id="advisorEmail"
                                                name="advisorEmail"
                                                value={formData.advisorEmail}
                                                onChange={handleFormChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                placeholder="Enter the advisor's email"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* client information */}
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="bg-primary px-4 py-3">
                                    <h5 className="text-white font-semibold">Client Information</h5>
                                </div>
                                <div className="p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="clientName" className="block text-sm font-semibold text-gray-700 mb-1">
                                                Name<span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="clientName"
                                                name="clientName"
                                                value={formData.clientName}
                                                onChange={handleFormChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                placeholder="Enter the client's name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="clientCompany" className="block text-sm font-semibold text-gray-700 mb-1">
                                                Company<span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="clientCompany"
                                                name="clientCompany"
                                                value={formData.clientCompany}
                                                onChange={handleFormChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                placeholder="Enter the client's company"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* company information */}
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="bg-primary px-4 py-3">
                                    <h5 className="text-white font-semibold">Company Information</h5>
                                </div>
                                <div className="p-4 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="adminFee" className="block text-sm font-semibold text-gray-700 mb-1">
                                                Admin Fee<span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <select
                                                id="adminFee"
                                                name="adminFee"
                                                value={formData.adminFee}
                                                onChange={handleFormChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                required
                                            >
                                                <option value="" disabled>Choose admin fee</option>
                                                {[5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10].map(fee => (
                                                    <option key={fee} value={fee}>{fee.toFixed(1)}%</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="province" className="block text-sm font-semibold text-gray-700 mb-1">
                                                Province<span className="text-red-500 ml-0.5">*</span>
                                            </label>
                                            <select
                                                id="province"
                                                name="province"
                                                value={formData.province}
                                                onChange={handleFormChange}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                                                required
                                            >
                                                <option value="" disabled>Select a province</option>
                                                <option value="AB">Alberta</option>
                                                <option value="BC">British Columbia</option>
                                                <option value="MB">Manitoba</option>
                                                <option value="NB">New Brunswick</option>
                                                <option value="NL">Newfoundland and Labrador</option>
                                                <option value="NT">Northwest Territories</option>
                                                <option value="NS">Nova Scotia</option>
                                                <option value="NU">Nunavut</option>
                                                <option value="ON">Ontario</option>
                                                <option value="PE">Prince Edward Island</option>
                                                <option value="QC">Quebec</option>
                                                <option value="SK">Saskatchewan</option>
                                                <option value="YT">Yukon</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* plan selection */}
                                    <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                                        <h6 className="font-semibold text-gray-900 mb-3">Choose a Plan</h6>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    id="HSA"
                                                    value="HSA"
                                                    onChange={handleCheckboxChange}
                                                    checked={isChecked("HSA")}
                                                    disabled={isDisabled("HSA")}
                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:opacity-50"
                                                />
                                                <span className="text-sm text-gray-700">Health Spending Account (HSA)</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    id="LSA"
                                                    value="LSA"
                                                    onChange={handleCheckboxChange}
                                                    checked={isChecked("LSA")}
                                                    disabled={isDisabled("LSA")}
                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:opacity-50"
                                                />
                                                <span className="text-sm text-gray-700">Lifestyle Spending Account (LSA)</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    id="CoreHealth"
                                                    value="CoreHealth"
                                                    onChange={handleCheckboxChange}
                                                    checked={isChecked("CoreHealth")}
                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                />
                                                <span className="text-sm text-gray-700">CoreHealth+ Account</span>
                                            </label>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    id="Flex"
                                                    value="Flex"
                                                    onChange={handleCheckboxChange}
                                                    checked={isChecked("Flex")}
                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                />
                                                <span className="text-sm text-gray-700">Flexible Spending Account (HSA & LSA)</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    id="Fixed"
                                                    value="Fixed"
                                                    onChange={handleCheckboxChange}
                                                    checked={isChecked("Fixed")}
                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                />
                                                <span className="text-sm text-gray-700">Fixed Spending Account (HSA & LSA)</span>
                                            </label>
                                        </div>

                                        {planAccounts.map((account, index) => (
                                            <PlanEstimates
                                                key={`${account.accountType}-${index}`}
                                                account={account}
                                                onUpdate={(updatedAccount) => updatePlanAccount(index, updatedAccount)}
                                                adminFee={formData.adminFee}
                                                province={formData.province}
                                                onPercentageUpdate={handleUpdatePercentage}
                                            />
                                        ))}
                                    </div>

                                    <div className="flex justify-center pt-2">
                                        <PDFGenerator
                                            proposal={formData}
                                            customPercentage={customPercentage}
                                            currentUser={currentUser}
                                            showLabel={true}
                                            onSessionExpired={onSessionExpired}
                                            onTokenRefresh={onTokenRefresh}
                                            onQuoteSaved={handleSubmit}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(QuoteEditTool);
